import type { ILogService, LogSeverityLevel } from '@common/services/log/log.service.interface';
import envConfig from '@scoring-web/configs/env/env.config';
import userStore from '@scoring-web/modules/user/stores/user.store';

export default class LogService implements ILogService {
  sentry: Awaited<ReturnType<typeof this.initialize>> | undefined;

  isInitialized = false;

  constructor() {
    this.captureError = this.captureError.bind(this);
    this.captureText = this.captureText.bind(this);

    this.initialize();
  }

  async initialize() {
    if (!envConfig.sentryDsnUrl) return undefined;

    const { captureException, captureMessage, getCurrentScope, init, setTag } = await import(
      '@sentry/react'
    );
    this.isInitialized = !!init({
      dsn: envConfig.sentryDsnUrl,
      environment: envConfig.sentryEnvironment,
      sendDefaultPii: true,
    });

    const sentry = { captureException, captureMessage, getCurrentScope, setTag };
    this.sentry = sentry;

    return sentry;
  }

  captureError(error: Error, extra?: Record<string, unknown>, tags?: Record<string, string>): void {
    if (!this.isInitialized) return;

    this.sentry?.captureException(error, { extra, tags, user: userStore.state?.user });
  }

  captureText(
    text: string,
    severity?: LogSeverityLevel,
    extra?: Record<string, unknown>,
    tags?: Record<string, string>,
  ): void {
    if (!this.isInitialized) return;

    this.sentry?.captureMessage(text, {
      extra,
      level: severity,
      tags,
      user: userStore.state?.user,
    });
  }

  setTag(key: string, value: string): void {
    if (!this.isInitialized) return;

    this.sentry?.setTag(key, value);
  }

  setTags(tags: Record<string, string>): void {
    if (!this.isInitialized) return;

    Object.entries(tags).forEach(([key, value]) => {
      this.sentry?.setTag(key, value);
    });
  }

  clearTag(key: string): void {
    if (!this.isInitialized) return;

    this.sentry?.getCurrentScope().setTag(key, undefined);
  }

  clearAllTags(): void {
    if (!this.isInitialized) return;

    this.sentry?.getCurrentScope().clear();
  }
}
